/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import { API_URL } from "./Environment";
import { isEntraWorkSchoolSignInEnabled } from "./RuntimeConfig";

const b2cAuthorityHost = "https://auth.synergistpro.io";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
    names: {
        signUpSignIn: "B2C_1_ALL_IN_ONE",
        signInAcceptInvitation: "B2C_1_SignIn_AcceptInvitation",
        signUp: "B2C_1_Signup_NewOrg",
        signIn: "B2C_1_SignIn",
        entraWorkSchoolSignIn: "B2C_1A_SynergistLinkSignIn",
        forgotPassword: "B2C_1_RESET",
        editProfile: "B2C_1_PROFILE"
    },
    authorities: {
        signUpSignIn: {
            authority: `${b2cAuthorityHost}/synergistprob2c.onmicrosoft.com/B2C_1_ALL_IN_ONE`,
        },
        signInAcceptInvitation: {
            authority: `${b2cAuthorityHost}/synergistprob2c.onmicrosoft.com/B2C_1_SignIn_AcceptInvitation`,
        },
        signUp: {
            authority: `${b2cAuthorityHost}/synergistprob2c.onmicrosoft.com/B2C_1_Signup_NewOrg`,
        },
        signIn: {
            authority: `${b2cAuthorityHost}/synergistprob2c.onmicrosoft.com/B2C_1_SignIn`,
        },
        entraWorkSchoolSignIn: {
            authority: `${b2cAuthorityHost}/synergistprob2c.onmicrosoft.com/B2C_1A_SynergistLinkSignIn`,
        },
        forgotPassword: {
            authority: `${b2cAuthorityHost}/synergistprob2c.onmicrosoft.com/B2C_1_RESET`,
        },
        editProfile: {
            authority: `${b2cAuthorityHost}/synergistprob2c.onmicrosoft.com/B2C_1_PROFILE`
        }
    },
    authorityDomain: "auth.synergistpro.io"
}

export const primarySignInPolicy = isEntraWorkSchoolSignInEnabled()
    ? {
        name: b2cPolicies.names.entraWorkSchoolSignIn,
        authority: b2cPolicies.authorities.entraWorkSchoolSignIn.authority,
        mode: 'entra-work-school' as const,
    }
    : {
        name: b2cPolicies.names.signIn,
        authority: b2cPolicies.authorities.signIn.authority,
        mode: 'legacy' as const,
    };

const defaultMsalAuthority = primarySignInPolicy.mode === 'entra-work-school'
    ? primarySignInPolicy.authority
    : b2cPolicies.authorities.signUpSignIn.authority;


/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {
    auth: {
        clientId: "f9f4ab36-a0a1-4fe8-9ca4-27661aac4498", // This is the ONLY mandatory field that you need to supply.
        // Preserve the existing SUSI default when the cutover is off. Ordinary
        // Login still uses signInRequest below; the custom authority becomes
        // the default only while the guarded cutover is enabled.
        authority: defaultMsalAuthority,
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: "/", // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                }
            }
        }
    }
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
    apiSynergist: {
        endpoint: process.env.NODE_ENV === 'development' ? "https://localhost:5079/Synergist.WebServices" : API_URL,
        scopes: ["https://synergistprob2c.onmicrosoft.com/d1df35f1-4cc7-425a-b8f5-8144dc5bbc98/webservice"], // e.g. api://xxxxxx/access_as_user
    },
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
 export const loginRequest = {
    scopes: [...protectedResources.apiSynergist.scopes] //, 'openid', 'offline_access'] these are auto-appended when using the library
};

export const signUpRequest = {
    authority: b2cPolicies.authorities.signUp.authority,
    scopes: loginRequest.scopes,
    redidirectUrl: process.env.NODE_ENV === 'development' ? "https://localhost:3000/CreatedOrganization" : `${API_URL}/CreatedOrganization`
}

/** Identity-first new-company entry point. Azure must enable both Microsoft
 * federation and local email sign-up on this combined policy before release. */
export const onboardingRequest = {
    authority: b2cPolicies.authorities.signUpSignIn.authority,
    scopes: loginRequest.scopes,
};

export const signInAcceptInvitationRequest = {
    authority: b2cPolicies.authorities.signInAcceptInvitation.authority, 
    scopes: loginRequest.scopes 
}

export const signInRequest = {
    authority: primarySignInPolicy.authority,
    scopes: loginRequest.scopes 
}

export const forgotPasswordRequest = {
    authority: b2cPolicies.authorities.forgotPassword.authority, 
    scopes: loginRequest.scopes 
}

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
    graphMeEndpoint: "https://graph.microsoft.com/v1.0/me"
};

export default this;
